import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import PrimeVue from 'primevue/config'
import Tooltip from 'primevue/tooltip'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import { clerkPlugin } from '@clerk/vue'
import { isDevMode } from './composables/devAuth.js'
import { isCefSharpUpdateRejection } from './utils/cefSharpRejection.js'
import { cleanupLocalDevPwa, isLocalhostHostname, shouldRegisterPwa } from './utils/pwaRegistration.js'
import { notifyPwaUpdateReady, setPwaUpdateHandler } from './composables/usePwaUpdate.js'
import { clearVitePreloadReloadGuard, installVitePreloadRecovery } from './utils/vitePreloadRecovery.js'
import { applyPendingShareTargetUpdate } from './utils/shareTargetUpdate.js'
import { i18n } from './i18n'
import { bootstrapLocale } from './composables/useLocale.js'
import { captureSignupReferrer } from './utils/signupReferrer.js'
import { capturePromoCode } from './utils/promoCode.js'
import { captureImportIntent } from './utils/importIntent.js'
import { persistLastRoute, resolveLaunchRoute } from './utils/lastRoute.js'
// Sentry is lazy-loaded after app mount for faster startup (518KB deferred)

// PrimeIcons
import 'primeicons/primeicons.css'

// Quill Editor styles are loaded lazily alongside the Editor component
// (see Bookmarks.vue / BookmarkDialog.vue). Keeping them out of the
// initial CSS bundle saves ~50kB on the /bookmarks critical path.

import './fonts.css'

// Global CSS for better layout
import './style.css'

// Mobile Drag and Drop Polyfill
import { polyfill } from "mobile-drag-drop";
import { scrollBehaviourDragImageTranslateOverride } from "mobile-drag-drop/scroll-behaviour";
import "mobile-drag-drop/default.css";

polyfill({
    dragImageCenterOnTouch: true,
    holdToDrag: 300, // Long press to drag (300ms)
    dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride,
    // Fix for Chrome DevTools Device Mode: Force polyfill if touch events are present
    // because Chrome reports native DnD support but touch inputs don't trigger it.
    forceApply: true // Force apply for debugging/dev mode testing
});

// Debug polyfill status
window.addEventListener('touchmove', function() {}, { passive: false }); // Helpful for some iOS versions to allow preventing default

// An old tab can reference hashed lazy-route assets removed by a newer deploy.
// Install recovery before the router can request any of those route modules.
installVitePreloadRecovery()

import App from './App.vue'

// Keep non-current routes off the initial /bookmarks critical path.
const Bookmarks = () => import('./views/Bookmarks.vue')
const Dashboard = () => import('./views/Dashboard.vue')
const Tags = () => import('./views/Tags.vue')
const Feeds = () => import('./views/Feeds.vue')
const Rules = () => import('./views/Rules.vue')
const SignIn = () => import('./views/SignIn.vue')
const SignUp = () => import('./views/SignUp.vue')
const SsoCallback = () => import('./views/SsoCallback.vue')
const VideoBookmarkDemo = () => import('./views/VideoBookmarkDemo.vue')
const AnalyzingCardDemo = () => import('./views/AnalyzingCardDemo.vue')
const PublicView = () => import('./views/PublicView.vue')
const PublicKnowledgeView = () => import('./views/PublicKnowledgeView.vue')
const EmojiPickerDemo = () => import('./views/EmojiPickerDemo.vue')
const SuccessView = () => import('./views/SuccessView.vue')
const AcceptInvitation = () => import('./views/AcceptInvitation.vue')

const routes = [
  // '/' is the PWA start_url: a bare launch reopens the last view (see lastRoute.js)
  { path: '/', redirect: (to) => resolveLaunchRoute(to, router) },
  { path: '/bookmarks', component: Bookmarks, meta: { requiresAuth: true } },
  { path: '/invitations/:token', component: AcceptInvitation, meta: { requiresAuth: true, noLastRoutePersist: true } },
  { path: '/dashboard', component: Dashboard, meta: { requiresAuth: true } },
  { path: '/demo/emoji-demo', component: EmojiPickerDemo, meta: { requiresAuth: true } },
  { path: '/tags', component: Tags, meta: { requiresAuth: true } },
  { path: '/feeds', component: Feeds, meta: { requiresAuth: true } },
  { path: '/rules', component: Rules, meta: { requiresAuth: true } },
  { path: '/knowledge', component: () => import('./views/Knowledge.vue'), meta: { requiresAuth: true } },
  { path: '/knowledge/review', component: () => import('./views/Review.vue'), meta: { requiresAuth: true } },
  { path: '/knowledge/review/history', component: () => import('./views/ReviewHistory.vue'), meta: { requiresAuth: true } },
  { path: '/knowledge/:path(.*)*', component: () => import('./views/Knowledge.vue'), meta: { requiresAuth: true } },
  { path: '/review', redirect: '/knowledge/review' },
  { path: '/notifications', component: () => import('./views/NotificationsView.vue'), meta: { requiresAuth: true } },
  { path: '/admin', component: () => import('./views/Admin.vue'), meta: { requiresAuth: true } },
  // Admin sub-sections are deep-linkable: /admin/outreach, /admin/ads, … The
  // segment is validated in the view (src/utils/adminRoute.js), not by a route
  // regex, so an unknown slug renders the default tab instead of a blank page —
  // and main.js stays free of the admin-only tab list.
  { path: '/admin/:tab', component: () => import('./views/Admin.vue'), meta: { requiresAuth: true } },
  { path: '/highlights', component: () => import('./views/Highlights.vue'), meta: { requiresAuth: true } },
  { path: '/share', component: Bookmarks, meta: { requiresAuth: true, isShareTarget: true, noLastRoutePersist: true } },
  { path: '/save', component: Bookmarks, meta: { requiresAuth: true, isShareTarget: true, noLastRoutePersist: true } },
  { path: '/public/knowledge/:shareId', component: PublicKnowledgeView, meta: { requiresAuth: false } },
  { path: '/public/:type/:shareId', component: PublicView, meta: { requiresAuth: false } },
  { path: '/@:username/:slug', component: PublicView, meta: { requiresAuth: false } },
  { path: '/@:username', component: () => import('./views/ProfileView.vue'), meta: { requiresAuth: false } },
  { path: '/discover', component: () => import('./views/DiscoverView.vue'), meta: { requiresAuth: false } },
  { path: '/discover/:filter(collections|knowledge)', component: () => import('./views/DiscoverView.vue'), meta: { requiresAuth: false } },
  { path: '/embed/knowledge/:shareId', component: () => import('./views/KnowledgeEmbedView.vue'), meta: { requiresAuth: false } },
  { path: '/embed/:type/:shareId', component: () => import('./views/EmbedView.vue'), meta: { requiresAuth: false } },
  // These MUST stay above the /sign-in and /sign-up catch-alls. Without them a
  // Clerk OAuth return (Sign in with Apple) matched the catch-all, which renders
  // the Mac email/password form — so the reviewer was asked for an email and a
  // password right after using Sign in with Apple (Guideline 4, 2026-07-14).
  { path: '/sign-in/sso-callback', component: SsoCallback, meta: { noLastRoutePersist: true } },
  { path: '/sign-up/sso-callback', component: SsoCallback, meta: { noLastRoutePersist: true } },
  { path: '/sign-in/:catchAll(.*)*', component: SignIn },
  { path: '/sign-up/:catchAll(.*)*', component: SignUp },
  { path: '/demo/video-bookmarks', component: VideoBookmarkDemo, meta: { requiresAuth: false } },
  { path: '/demo/analyzing', component: AnalyzingCardDemo, meta: { requiresAuth: false } },
  { path: '/success.html', component: SuccessView, meta: { requiresAuth: true, noLastRoutePersist: true } }
]

// MUST run before createWebHistory(): the history snapshots the current URL the
// moment it is created, and the router then restores that snapshot on its
// initial navigation. Capturing later — even a few lines down in the async boot
// function with the other two param captures — meant `?import=` came back after
// being stripped, so a reload re-fired it and re-opened the import dialog.
// Verified in a browser: this is the only ordering that actually consumes once.
captureImportIntent()

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard to protect authenticated routes
router.beforeEach((to, from, next) => {
  // Redirect handling is done centrally in App.vue where Clerk state is available
  next()
})

// Persist the last visited authenticated route so the app can restore it on next open
router.afterEach(persistLastRoute)

const app = createApp(App)

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#E3F2FD',
      100: '#BBDEFB',
      200: '#90CAF9',
      300: '#64B5F6',
      400: '#42A5F5',
      500: '#2196F3',
      600: '#1E88E5',
      700: '#1976D2',
      800: '#1565C0',
      900: '#0D47A1',
      950: '#0D47A1'
    }
  }
})

async function bootstrap() {
  // A share sheet is the one entry point whose correctness depends on running
  // the newest bundle (the /share URL parser) and the one that can never act on
  // the update prompt — it prefills and redirects on cold boot. Apply a waiting
  // worker here, before anything renders, so the share never runs a stale
  // parser. Route meta is the single source of truth for which paths those are.
  // Contained: bootstrap() is called bare (no .catch), so anything that throws
  // here would leave the app unmounted. A share-sheet nicety must never be able
  // to white-screen every route.
  let shareTargetUpdating = false
  try {
    const shareTargetEntry = router.resolve(window.location.pathname).meta?.isShareTarget === true
    shareTargetUpdating = await applyPendingShareTargetUpdate({ isShareTarget: shareTargetEntry })
  } catch (error) {
    console.warn('Share target update check failed:', error)
  }
  if (shareTargetUpdating) {
    // Reloading into the new bundle — don't mount the outgoing one.
    return
  }

  // Persist ad attribution (utm_* / ref / click ids) from the entry URL before
  // anything can navigate: App.vue's auth redirect does a bare router.push that
  // drops the query string, so capturing only in SignUp.vue loses UTMs that land
  // on the app root. URL params only — the document.referrer and direct-visit
  // fallbacks run in the auth views (SignIn.vue, SignUp.vue) so signed-in users'
  // referrers aren't recorded on every app load.
  captureSignupReferrer({ includeDocumentReferrer: false })
  // Same boot-time constraint as above: App.vue's auth redirect drops the query
  // string, so ?promo=NESSLABS must be stored before routing starts.
  capturePromoCode()
  // The third param capture, ?import=chrome from the marketing site's free
  // bookmarks tool, is NOT here: it has to run before createRouter() above.
  // See the comment there.

  // Resolve the UI locale BEFORE any plugin renders text: lazy-loads the de
  // catalog when needed, sets <html lang>, and decides the Clerk localization.
  // Chain: backendUser.language (post-auth, App.vue) → localStorage → ?lang →
  // lf_locale cookie (German marketing site) → browser → 'en'.
  const bootLocale = await bootstrapLocale()

  // Configure Clerk (skip in dev mode — auth is mocked)
  if (!isDevMode) {
    const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

    if (!clerkPublishableKey) {
      throw new Error('Missing Clerk Publishable Key')
    }

    // Clerk UIs localize at boot only; a profile language switch reaches them
    // on the next load (app strings switch live via vue-i18n). Each pack loads
    // from its own lazy chunk so English boots ship zero extra bytes. Literal
    // import specifiers (not a templated path) so Vite can pre-bundle each pack;
    // es/pt resolve to the European variants (es-ES / pt-PT).
    const clerkLocaleLoaders = {
      de: () => import('@clerk/localizations/de-DE').then((m) => m.deDE),
      fr: () => import('@clerk/localizations/fr-FR').then((m) => m.frFR),
      es: () => import('@clerk/localizations/es-ES').then((m) => m.esES),
      pt: () => import('@clerk/localizations/pt-PT').then((m) => m.ptPT),
      it: () => import('@clerk/localizations/it-IT').then((m) => m.itIT)
    }
    let localization
    const clerkLoader = clerkLocaleLoaders[bootLocale]
    if (clerkLoader) {
      localization = await clerkLoader()
    }

    app.use(clerkPlugin, {
      publishableKey: clerkPublishableKey,
      signInUrl: '/sign-in',
      signUpUrl: '/sign-up',
      signInFallbackRedirectUrl: '/bookmarks',
      signUpFallbackRedirectUrl: '/bookmarks',
      // The Smart CAPTCHA (Turnstile) renders in a cross-origin iframe that
      // CSS color-scheme cannot reach; its default 'auto' theme follows the
      // OS, painting a dark widget on the hard-white Mac auth card under
      // macOS Dark Mode. The auth surfaces are light in both app themes.
      appearance: { captcha: { theme: 'light' } },
      localization
    })
  }

  app.use(PrimeVue, {
    theme: {
      preset: MyPreset,
      options: {
        prefix: 'p',
        darkModeSelector: '.dark-theme',
        cssLayer: false
      }
    },
    ripple: true,
    // a11y (WCAG 4.1.2): PrimeVue's icon-only Tree/TreeTable expand-collapse
    // toggles ship without an accessible name. Give them one globally (additive
    // attribute only — no behavior change). Fixes the bulk of the button-name
    // violations on /bookmarks (collections tree) and /tags (tag tree table).
    // Evaluated at boot in the boot locale (same next-load semantics as Clerk).
    pt: {
      tree: { nodeToggleButton: { 'aria-label': i18n.global.t('app.bookmarksTree.toggleSubtree') } },
      treetable: { rowToggleButton: { 'aria-label': i18n.global.t('app.bookmarksTree.toggleSubtree') } }
    }
  })
  app.use(i18n)
  app.use(router)
  app.use(ToastService)
  app.use(ConfirmationService)
  app.directive('tooltip', Tooltip)

  app.mount('#app')
  clearVitePreloadReloadGuard()

  // Initialize Sentry error monitoring after mount for faster startup
  // (no-op if VITE_SENTRY_DSN is not set)
  const initSentryLazy = () => {
    import('./services/sentry.js').then(({ initSentry }) => {
      initSentry(app, { router })
    })
  }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initSentryLazy)
  } else {
    setTimeout(initSentryLazy, 2000)
  }

  // Product analytics, same deferred treatment as Sentry
  // (no-op if VITE_PLAUSIBLE_SRC is not set)
  const initAnalyticsLazy = () => {
    // Catch, not just then: after a deploy the previous build's chunk is gone,
    // and an unhandled rejection here would be reported as an app error.
    import('./services/analytics.js').then(({ initAnalytics }) => {
      initAnalytics()
    }).catch(() => { })
  }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initAnalyticsLazy)
  } else {
    setTimeout(initAnalyticsLazy, 2000)
  }

  const currentHostname = window.location.hostname
  const registerPwa = shouldRegisterPwa({
    isProd: import.meta.env.PROD,
    hostname: currentHostname
  })

  if (registerPwa) {
    import('virtual:pwa-register').then(({ registerSW }) => {
      // Prompt mode: a new build's SW waits until the user opts in. We surface
      // the reload via PwaUpdatePrompt.vue instead of force-reloading the page.
      const updateSW = registerSW({
        immediate: true,
        onNeedRefresh() {
          notifyPwaUpdateReady()
        },
        onRegisteredSW(swUrl, registration) {
          // Installed-PWA tabs rarely navigate, so the browser's built-in SW
          // update check may not run for a long time. Poll hourly so a new
          // deploy surfaces the reload prompt without a manual refresh.
          if (registration) {
            setInterval(() => {
              registration.update().catch(() => {})
            }, 60 * 60 * 1000)
          }
        },
        onRegisterError(error) {
          console.warn('Service worker registration failed:', error)
        }
      })
      setPwaUpdateHandler(updateSW)
    })
  } else if (isLocalhostHostname(currentHostname)) {
    cleanupLocalDevPwa().catch((error) => {
      console.warn('Failed to clean up localhost service workers:', error)
    })
  }

  // Remove server-rendered SEO content block once Vue has hydrated the page
  document.getElementById('ssr-content')?.remove()
}

bootstrap()

// Catch ClerkJS transient network errors that bubble up as unhandled rejections.
// Clerk's <SignIn> UI already shows "Please try again" — suppress the global error.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  // `reason` is usually an Error, but some code paths (and a few browsers)
  // reject with a plain string. Coerce so the regex checks still apply.
  const msg = (reason && typeof reason === 'object' && typeof reason.message === 'string')
    ? reason.message
    : (typeof reason === 'string' ? reason : '')
  if (msg.startsWith('ClerkJS: Network error at ')) {
    event.preventDefault()
    console.warn('Suppressed transient Clerk network error:', msg)
  }
  // Safari iOS: extension content-script calls to chrome.runtime.sendMessage()
  // reject with "Tab not found" when the background service worker is suspended.
  // The rejection leaks to the page's global handler — suppress it.
  if (/Invalid call to runtime\.sendMessage\(\)/.test(msg)) {
    event.preventDefault()
  }
  // CefSharp-injected safe-link scanners can reject from their page bridge
  // when a previously bound object disappears. This is not application code;
  // keep the signature exact so real object/update failures still surface.
  if (isCefSharpUpdateRejection(msg)) {
    event.preventDefault()
  }
  // WebAuthn / passkey: browsers without public key credential support throw
  // NotSupportedError when Clerk probes for passkeys during sign-in.
  // Clerk falls back to other methods — suppress the console noise.
  // Covers two known variants:
  //   1. "The user agent does not support public key credentials."
  //   2. "Resident credentials or empty 'allowCredentials' lists are not
  //      supported at this time." (Chromium ≥ 138 / non-resident key path)
  if (/The user agent does not support public key credentials|Resident credentials or empty .allowCredentials. lists are not supported/.test(msg)) {
    event.preventDefault()
    console.warn('Suppressed WebAuthn NotSupportedError:', msg)
  }
  // Clerk domain mismatch: users who visit the Cloudflare Pages deployment URL
  // (linkflare-app.pages.dev) instead of app.linkflare.io get this error because
  // production keys are restricted to linkflare.io. The middleware issues a 301
  // redirect, but cached pages can still trigger this before the redirect fires.
  if (/Clerk: Production Keys are only allowed for domain/.test(msg)) {
    event.preventDefault()
    console.warn('Suppressed Clerk domain mismatch error (pages.dev):', msg)
  }
  // Clerk: Failed to load Clerk. Two known code variants:
  //   - code="failed_to_load_clerk_js_timeout" — script didn't finish loading
  //     within Clerk's internal timeout (slow network).
  //   - code="failed_to_load_clerk_js" — the <script> element fired an `error`
  //     event before any load (ad/script blocker blocking clerk.linkflare.io,
  //     CDN failure, or DNS failure). The cause chain includes an inner
  //     "failed to load script: https://clerk.linkflare.io/..." Error.
  // Clerk's UI handles the failure on its own; the unhandled rejection just
  // adds Sentry noise we can't act on.
  if (
    /Clerk: Failed to load Clerk/.test(msg) ||
    /^failed to load script: https?:\/\/[^/]*clerk\./.test(msg) ||
    (reason && typeof reason === 'object' && typeof reason.code === 'string' && reason.code.startsWith('failed_to_load_clerk_js'))
  ) {
    event.preventDefault()
    console.warn('Suppressed Clerk failed-to-load error:', msg)
  }
})
